import {
  Header as SharedHeader,
  UserProfile,
  UserProfileMenuItem,
} from "@amzn/it-shop-shared-components";
import {
  PageHeaderDropdown,
  PageHeaderDropdownLink,
  PageHeaderLink,
  PageHeaderList,
} from "@amzn/stencil-react-components/page";
import IconQuestionCircle from "@amzn/stencil-react-icons/icons/icon-question-circle";
import { trackUxmCustomEvent } from "../../helpers/analytics-helper";
import { Paths } from "../../paths/paths";
import { getString } from "../../uistringlabels/ui-string-utils";
import { IT_SHOP_URL, PERMISSIONS } from "../../../config";
import { useSession } from "../../providers/session-provider";
import { useDocumentTitle } from "../../hooks/use-document-title";
import { userInfoQueryOptions } from "../../../services/user-info/user-info.query";
import type { AmazonianInfo } from "../../../services/user-info/user-info.types";
import { useQuery } from "@tanstack/react-query";
import React, { ReactNode } from "react";

const SOFTWARE_SUPPORT_URL = "https://w.amazon.com/bin/view/ITMarketplaceSoftwareOrdering";
const ORG_SOFTWARE_EXPENSE_URL = "https://appsense.ee.amazon.dev/orgSummary";
const FAQ_URL = `${IT_SHOP_URL}/faq`;

const track = (touchpoint: string) => trackUxmCustomEvent(touchpoint, {});

export const Header: React.FC = () => {
  const { username, hasPermission } = useSession();
  const { data: userInfo } = useQuery<AmazonianInfo, Error>(userInfoQueryOptions);
  const showLcrLink = hasPermission(PERMISSIONS.USE_LCR) && userInfo?.jobInfo.isOPSEmployee === false;
  useDocumentTitle("IT Shop Hardware");

  const profileMenuItems: UserProfileMenuItem[] = [
    {
      key: "orders",
      label: getString("header.orders"),
      href: Paths.orders.url,
      onClick: () => track("header-your-orders-link"),
      testId: "header-orders-link",
      dataCy: "orders-link",
    },
    {
      key: "org-software-expense",
      label: getString("header.orgSoftwareExpense"),
      href: ORG_SOFTWARE_EXPENSE_URL,
      onClick: () => track("header-org-software-expense"),
      target: "_blank",
    },
  ];

  const actionItems: ReactNode[] = [
    ...(showLcrLink
      ? [
          <PageHeaderLink
            key="hardware-link"
            href={Paths.myHardware.url}
            className="header-primary-text"
            data-cy="hardware-link"
            onClick={() => track("header-hardware")}
          >
            {getString("header.hardware")}
          </PageHeaderLink>,
        ]
      : []),
    <PageHeaderDropdown
      key="help-dropdown"
      className="header-primary-text"
      dataTestId="support-dropdown"
      data-cy="support-dropdown"
      icon={<IconQuestionCircle />}
      items={[
        <PageHeaderDropdownLink
          key="softwareSupport"
          href={SOFTWARE_SUPPORT_URL}
          target="_blank"
          dataTestId="software-support"
          onClick={() => track("header-software-support")}
        >
          {getString("header.softwareSupport")}
        </PageHeaderDropdownLink>,
        <PageHeaderDropdownLink
          key="hardwareSupport"
          href={`${Paths.orders.url}?flyoutHardwareSupport=true`}
          dataTestId="hardware-support"
          onClick={() => track("header-hardware-support")}
        >
          {getString("header.hardwareSupport")}
        </PageHeaderDropdownLink>,
      ]}
    />,
    <PageHeaderLink
      key="faq-link"
      href={FAQ_URL}
      data-cy="faq-link"
      onClick={() => track("header-faq")}
      className="header-primary-text"
    >
      {getString("header.faqs")}
    </PageHeaderLink>,
    <UserProfile
      key="user-profile"
      username={username}
      strings={{
        pictureAriaLabel: getString("userProfile.pictureAriaLabel", { username }),
        dropdownLabel: getString("header.yourOrders"),
      }}
      menuItems={profileMenuItems}
    />,
  ];

  return (
    <SharedHeader logoText={getString("logoText")} logoHref={IT_SHOP_URL}>
      <PageHeaderList data-cy="header-items" items={actionItems} />
    </SharedHeader>
  );
};
